import React, { createContext, useContext, useEffect, useCallback, useRef, useState } from 'react';
import useProgress from '../hooks/useProgress';

const ProgressContext = createContext(null);

const API_URL = process.env.REACT_APP_BACKEND_URL;
const DEVICE_KEY = 'blockquest_device_id';
const DEVICE_TOKEN_KEY = 'blockquest_device_token';
const LAST_ACTIVE_KEY = 'blockquest_last_active';
const LOCAL_SCORES_KEY = 'blockquest_local_scores';
const PROGRESS_KEY = 'blockquest_progress';
const RETURNING_THRESHOLD_MS = 4 * 60 * 60 * 1000; // 4 hours

const getDeviceId = () => {
  let id = localStorage.getItem(DEVICE_KEY);
  if (!id) {
    id = crypto.randomUUID ? crypto.randomUUID() : 'dev-' + Math.random().toString(36).slice(2) + Date.now().toString(36);
    localStorage.setItem(DEVICE_KEY, id);
    // Clear stale token when new device ID is created
    localStorage.removeItem(DEVICE_TOKEN_KEY);
  }
  return id;
};

const fetchDeviceToken = async (deviceId) => {
  const cached = localStorage.getItem(DEVICE_TOKEN_KEY);
  if (cached) return cached;
  if (!API_URL) return '';
  try {
    const resp = await fetch(`${API_URL}/api/device/token`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ device_id: deviceId })
    });
    const data = await resp.json();
    if (data.token) {
      localStorage.setItem(DEVICE_TOKEN_KEY, data.token);
      return data.token;
    }
  } catch (e) { console.warn('Failed to fetch device token:', e); }
  return '';
};

/** Build headers with device token for authenticated requests */
const deviceHeaders = (token) => ({
  'Content-Type': 'application/json',
  ...(token ? { 'X-Device-Token': token } : {})
});

export const ProgressProvider = ({ children }) => {
  const progress = useProgress();
  const deviceId = useRef(getDeviceId());
  const deviceToken = useRef('');
  const syncTimeout = useRef(null);
  const [showWelcomeBack, setShowWelcomeBack] = useState(false);
  const [inactiveHours, setInactiveHours] = useState(0);

  // Ref to track current progress for use in mount-only effects
  const progressRef = useRef(progress);
  useEffect(() => { progressRef.current = progress; });

  // Fetch device token on mount
  useEffect(() => {
    fetchDeviceToken(deviceId.current).then(t => { deviceToken.current = t; });
  }, []);

  // Sync progress TO Supabase (debounced)
  const syncToCloud = useCallback(async (progressData) => {
    if (!API_URL) return;
    try {
      await fetch(`${API_URL}/api/progress/${deviceId.current}`, {
        method: 'PUT',
        headers: deviceHeaders(deviceToken.current),
        body: JSON.stringify({
          device_id: deviceId.current,
          progress: {
            xp: progressData.xp || 0,
            quests_completed: progressData.questsCompleted || [],
            badges: progressData.badges || [],
            heroes_unlocked: progressData.heroesUnlocked || [],
            selected_avatar: progressData.selectedAvatar || 'gerry',
            daily_streak: progressData.dailyStreak || 0,
            last_daily_claim: progressData.lastDailyClaim || null,
            display_name: progressData.displayName || '',
          }
        })
      });
    } catch (e) {
      console.warn('Supabase sync failed (offline?)', e);
    }
  }, []);

  // Save display name to backend profile
  const saveDisplayName = useCallback(async (name) => {
    progress.setDisplayName(name);
    if (!API_URL) return;
    try {
      await fetch(`${API_URL}/api/profile/${deviceId.current}`, {
        method: 'PUT',
        headers: deviceHeaders(deviceToken.current),
        body: JSON.stringify({ display_name: name })
      });
    } catch (e) {
      console.warn('Profile sync failed', e);
    }
  }, [progress]);

  // Debounced sync - triggers 2s after last change
  const debouncedSync = useCallback((data) => {
    clearTimeout(syncTimeout.current);
    syncTimeout.current = setTimeout(() => syncToCloud(data), 2000);
  }, [syncToCloud]);

  // Watch for progress changes and sync
  useEffect(() => {
    if (progress.hasProgress) {
      debouncedSync(progress.progress);
    }
  }, [progress.progress, progress.hasProgress, debouncedSync]);

  // Track last_active & detect returning explorer (mount-only)
  useEffect(() => {
    const now = Date.now();
    const lastActive = parseInt(localStorage.getItem(LAST_ACTIVE_KEY) || '0', 10);

    // Read hasProgress directly from localStorage to avoid stale-closure dep
    let hasExisting = false;
    try {
      const saved = JSON.parse(localStorage.getItem(PROGRESS_KEY) || '{}');
      hasExisting = (saved.xp || 0) > 0 || (saved.questsCompleted || []).length > 0;
    } catch { /* no saved progress */ }

    if (lastActive > 0 && hasExisting) {
      const gap = now - lastActive;
      if (gap > RETURNING_THRESHOLD_MS) {
        setInactiveHours(Math.floor(gap / (60 * 60 * 1000)));
        setShowWelcomeBack(true);
      }
    }

    localStorage.setItem(LAST_ACTIVE_KEY, now.toString());

    const interval = setInterval(() => {
      localStorage.setItem(LAST_ACTIVE_KEY, Date.now().toString());
    }, 60000);

    return () => clearInterval(interval);
  }, []);

  // On mount: fetch from Supabase and merge (waits for device token)
  useEffect(() => {
    const fetchAndMerge = async () => {
      if (!API_URL) return;
      // Ensure token is available before making authed calls
      if (!deviceToken.current) {
        deviceToken.current = await fetchDeviceToken(deviceId.current);
      }
      const headers = { 'X-Device-Token': deviceToken.current };
      try {
        const resp = await fetch(`${API_URL}/api/progress/${deviceId.current}`, { headers });
        const data = await resp.json();
        if (data.progress && data.xp > 0) {
          const localXp = progressRef.current.progress.xp || 0;
          if (data.xp > localXp) {
            console.log('Restoring progress from cloud:', data.xp, 'XP');
          }
        }
        if (!progressRef.current.progress.displayName) {
          const profileResp = await fetch(`${API_URL}/api/profile/${deviceId.current}`, { headers });
          const profileData = await profileResp.json();
          if (profileData.display_name) {
            progressRef.current.setDisplayName(profileData.display_name);
          }
        }
      } catch (e) {
        console.warn('Could not fetch cloud progress', e);
      }
    };
    fetchAndMerge();
  }, []);

  const dismissWelcomeBack = useCallback(() => {
    setShowWelcomeBack(false);
  }, []);

  // Save a score to localStorage (called by games or hub)
  const saveLocalScore = useCallback((game, score, playerName) => {
    try {
      const existing = JSON.parse(localStorage.getItem(LOCAL_SCORES_KEY) || '[]');
      const name = playerName || progressRef.current.progress.displayName || 'Explorer';
      const idx = existing.findIndex(s => s.game === game);
      if (idx >= 0 && existing[idx].score >= score) return;
      const entry = { game, score, name, ts: Date.now(), synced: false };
      if (idx >= 0) {
        existing[idx] = entry;
      } else {
        existing.push(entry);
      }
      localStorage.setItem(LOCAL_SCORES_KEY, JSON.stringify(existing));
    } catch (e) {
      console.warn('Could not save local score', e);
    }
  }, []);

  // Auto-sync unsynced local scores to backend on mount
  useEffect(() => {
    const syncLocalScores = async () => {
      if (!API_URL) return;
      try {
        const scores = JSON.parse(localStorage.getItem(LOCAL_SCORES_KEY) || '[]');
        const unsynced = scores.filter(s => !s.synced);
        if (unsynced.length === 0) return;

        const name = progressRef.current.progress.displayName || 'Explorer';
        let changed = false;

        for (const s of unsynced) {
          try {
            const resp = await fetch(`${API_URL}/api/leaderboard/submit`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({
                device_id: deviceId.current,
                game: s.game,
                score: s.score,
                player_name: s.name || name,
              })
            });
            if (resp.ok) {
              s.synced = true;
              changed = true;
            }
          } catch (e) { console.warn('Score sync retry pending:', e); }
        }

        if (changed) {
          localStorage.setItem(LOCAL_SCORES_KEY, JSON.stringify(scores));
        }
      } catch (e) {
        console.warn('Local score sync failed', e);
      }
    };
    syncLocalScores();
  }, []);

  // Get local scores (for offline leaderboard)
  const getLocalScores = useCallback(() => {
    try {
      return JSON.parse(localStorage.getItem(LOCAL_SCORES_KEY) || '[]');
    } catch (e) { console.warn('Failed to read local scores:', e); return []; }
  }, []);

  const value = {
    ...progress,
    deviceId: deviceId.current,
    deviceToken: deviceToken.current,
    getDeviceToken: () => deviceToken.current,
    syncToCloud: () => syncToCloud(progress.progress),
    saveDisplayName,
    saveLocalScore,
    getLocalScores,
    showWelcomeBack,
    dismissWelcomeBack,
    inactiveHours,
  };

  return (
    <ProgressContext.Provider value={value}>
      {children}
    </ProgressContext.Provider>
  );
};

export const useProgressContext = () => {
  const ctx = useContext(ProgressContext);
  if (!ctx) throw new Error('useProgressContext must be used within ProgressProvider');
  return ctx;
};

export default ProgressContext;
